import { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Typography from '@mui/material/Typography'
import MuiIconButton from '@mui/material/IconButton'
import Close from '@mui/icons-material/Close'
import Users from '../Users'
import APIAccess from '../APIAccess'
import FolderMembersOverview from './FolderMembersOverview'
import { getCapabilities } from '../../apis/project'

interface IProps {
  open: boolean
  projectId: string | null
  onClose: () => void
}

type ManageTab = 'overview' | 'users' | 'api'

const USERS_PAGE_PARAM = 'folderUsersPage'
const API_PAGE_PARAM = 'folderApiPage'

const FolderAccessDialog = ({ open, projectId, onClose }: IProps) => {
  const intl = useIntl()
  const [, setSearchParams] = useSearchParams()

  const [manageTab, setManageTab] = useState<ManageTab>('overview')
  const [canGrantAccess, setCanGrantAccess] = useState<boolean | undefined>(
    undefined
  )

  const manageAccessLabel = intl.formatMessage({
    id: 'projectContent.manageAccess',
    defaultMessage: 'Folder Access'
  })

  useEffect(() => {
    if (!open || !projectId) return
    setManageTab('overview')
  }, [open, projectId])

  useEffect(() => {
    // The dialog stays mounted, so the previous folder's capability has to be
    // dropped on close too — otherwise a reopen renders a pass with it.
    setCanGrantAccess(undefined)

    if (!open || !projectId) {
      return
    }

    let cancelled = false

    getCapabilities(projectId)
      .then(capabilities => {
        if (!cancelled) {
          setCanGrantAccess(!!capabilities?.canGrantAccess)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCanGrantAccess(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [open, projectId])

  const handleClose = useCallback(() => {
    setSearchParams(
      prev => {
        const newParams = new URLSearchParams(prev)
        newParams.delete(USERS_PAGE_PARAM)
        newParams.delete(API_PAGE_PARAM)
        return newParams
      },
      { replace: true }
    )
    onClose()
  }, [onClose, setSearchParams])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          height: 'fit-content',
          pb: 8
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          pb: 1.5
        }}
      >
        <Typography variant="h5" component="span">
          {manageAccessLabel}
        </Typography>
        <MuiIconButton aria-label="Close" size="small" onClick={handleClose}>
          <Close />
        </MuiIconButton>
      </DialogTitle>

      <Divider />

      <Tabs
        value={manageTab}
        onChange={(_e, v: ManageTab) => setManageTab(v)}
        indicatorColor="secondary"
        textColor="primary"
        sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value="overview"
          label={intl.formatMessage({
            id: 'projectAccess.nav.overview',
            defaultMessage: 'Overview'
          })}
        />
        <Tab
          value="users"
          label={intl.formatMessage({
            id: 'projectAccess.nav.users',
            defaultMessage: 'Users'
          })}
        />
        <Tab
          value="api"
          label={intl.formatMessage({
            id: 'projectAccess.nav.api',
            defaultMessage: 'API access'
          })}
        />
      </Tabs>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {manageTab === 'overview' && projectId && (
          <FolderMembersOverview projectId={projectId} />
        )}
        {manageTab === 'users' && projectId && (
          <Users
            projectId={projectId}
            hideNav
            formsInDialog
            disableRowClick
            canManageMembers={canGrantAccess === true}
            pageParamName={USERS_PAGE_PARAM}
          />
        )}
        {manageTab === 'api' && projectId && (
          <APIAccess
            projectId={projectId}
            hideNav
            formsInDialog
            disableRowClick
            pageParamName={API_PAGE_PARAM}
          />
        )}
      </Box>
    </Dialog>
  )
}

export default FolderAccessDialog
